import * as React from "react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";

import {
    Button,
    CircularProgress,
    TextField,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Avatar,
    Box,
    Tooltip,
    Switch,
    Collapse,
    Select,
    MenuItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import SaveIcon from "@material-ui/icons/Save";
import EditIcon from "@material-ui/icons/Edit";
import CheckIcon from "@material-ui/icons/Check";
import CloseIcon from "@material-ui/icons/Close";
import BookmarkIcon from "@material-ui/icons/Bookmark";
import BookmarkBorderIcon from "@material-ui/icons/BookmarkBorder";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import CategoryBrandPanel from "./CategoryBrandPanel";

import { Notification, useNotify } from "react-admin";
import { API } from "../Constant";
import { getConfigGet, getConfigPatch } from "../getConfig";

const ENDPOINT = API + "/admin/shop-grid";

interface Category {
    id: number;
    category: string;
    display_name: string;
    category_logo: string | null;
    sort_order: number;
    is_pinned: boolean;
    is_active: boolean;
    platform: string;
    parent_category_id: number | null;
}

const PLATFORM_OPTIONS = [
    { value: "all", label: "All" },
    { value: "ios", label: "iOS only" },
    { value: "android", label: "Android only" },
];

const useStyles = makeStyles((theme) => ({
    main: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        padding: theme.spacing(3),
        backgroundColor: "#f5f5f5",
    },
    embedded: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(2),
    },
    hint: {
        color: "#666",
        marginBottom: theme.spacing(2),
    },
    chevronCell: { width: 40 },
    posCell: { width: 60, fontWeight: 600 },
    logoCell: { width: 64 },
    pinnedCell: { width: 80, textAlign: "center" as const },
    activeCell: { width: 80, textAlign: "center" as const },
    platformCell: { width: 130 },
    arrows: { width: 96, whiteSpace: "nowrap" },
    inactiveRow: { opacity: 0.5 },
    dragOverRow: { borderTop: "2px solid #2196f3" },
    nameInput: { minWidth: 260 },
    slugCell: { color: "#999", fontFamily: "monospace", fontSize: 12 },
    parentCell: { color: "#333" },
    topLevelCell: { color: "#bbb" },
    dirtyRow: { backgroundColor: "#fff8e1" },
}));

const ShopGridCategories = ({ embedded = false }: { embedded?: boolean }) => {
    const classes = useStyles();
    const notify = useNotify();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState<Category[]>([]);
    const [original, setOriginal] = useState<Record<number, Category>>({});
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingName, setEditingName] = useState("");
    const [expandedId, setExpandedId] = useState<number | null>(null);

    useEffect(() => {
        fetchCategories();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const config = getConfigGet({ resource: "categories" });
            const response: any = await axios.get(ENDPOINT, config);
            const raw: Category[] = response.data?.data || response.data || [];
            // Coerce is_pinned (DB may send 1/0) to a real boolean.
            const data = raw.map((c) => ({
                ...c,
                is_pinned: !!c.is_pinned,
                is_active: !!c.is_active,
                platform: c.platform || "all",
            }));
            setRows(data);
            const snapshot: Record<number, Category> = {};
            data.forEach((c) => (snapshot[c.id] = { ...c }));
            setOriginal(snapshot);
        } catch (error) {
            notify("Failed to load categories", "warning");
        } finally {
            setLoading(false);
        }
    };

    const dragItem = useRef<number | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    const reorder = (from: number, to: number) => {
        if (from === to) return;
        setRows((prev) => {
            const next = [...prev];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            return next;
        });
    };

    const onRowDragOver = (index: number) => {
        const from = dragItem.current;
        setDragOverIndex(index);
        if (from !== null && from !== index) {
            reorder(from, index);
            dragItem.current = index;
        }
    };

    const togglePin = (index: number) => {
        const next = [...rows];
        next[index] = { ...next[index], is_pinned: !next[index].is_pinned };
        setRows(next);
    };

    const toggleActive = (index: number) => {
        const next = [...rows];
        next[index] = { ...next[index], is_active: !next[index].is_active };
        setRows(next);
    };

    const changePlatform = (index: number, platform: string) => {
        const next = [...rows];
        next[index] = { ...next[index], platform };
        setRows(next);
    };

    const startEdit = (row: Category) => {
        setEditingId(row.id);
        setEditingName(row.display_name);
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingName("");
    };

    const confirmEdit = (index: number) => {
        const value = editingName.trim();
        if (value) {
            const next = [...rows];
            next[index] = { ...next[index], display_name: value };
            setRows(next);
        }
        cancelEdit();
    };

    const isDirty = (row: Category, index: number): boolean => {
        const orig = original[row.id];
        if (!orig) return true;
        return (
            orig.display_name !== row.display_name ||
            orig.sort_order !== index + 1 ||
            orig.is_pinned !== row.is_pinned ||
            orig.is_active !== row.is_active ||
            orig.platform !== row.platform
        );
    };

    const dirtyCount = rows.filter((r, i) => isDirty(r, i)).length;

    const parentName = (row: Category): string => {
        if (!row.parent_category_id) return "—";
        const parent = rows.find((r) => r.id === row.parent_category_id);
        return parent ? parent.display_name : `#${row.parent_category_id}`;
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const headers = getConfigPatch();
            const changed = rows
                .map((row, index) => ({ row, sortOrder: index + 1 }))
                .filter(({ row, sortOrder }) => {
                    const orig = original[row.id];
                    return (
                        !orig ||
                        orig.display_name !== row.display_name ||
                        orig.sort_order !== sortOrder ||
                        orig.is_pinned !== row.is_pinned ||
                        orig.is_active !== row.is_active ||
                        orig.platform !== row.platform
                    );
                });

            for (const { row, sortOrder } of changed) {
                await axios.patch(
                    `${ENDPOINT}/${row.id}?resource=category`,
                    {
                        sort_order: sortOrder,
                        display_name: row.display_name,
                        is_pinned: row.is_pinned,
                        is_active: row.is_active,
                        platform: row.platform,
                    },
                    { headers }
                );
            }

            notify(
                changed.length
                    ? `Saved ${changed.length} categor${changed.length === 1 ? "y" : "ies"}`
                    : "No changes to save",
                "info"
            );
            fetchCategories();
        } catch (error: any) {
            notify(error?.response?.data?.message || "Failed to save categories", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={embedded ? classes.embedded : classes.main}>
            <div className={classes.header}>
                {!embedded && <Typography variant="h5">Shop Grid — Categories</Typography>}
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                    disabled={saving || loading || dirtyCount === 0}
                    onClick={handleSave}
                >
                    {saving ? "Saving..." : `Save${dirtyCount ? ` (${dirtyCount})` : ""}`}
                </Button>
            </div>

            <Typography variant="body2" className={classes.hint}>
                Drag the handle to reorder, rename what users see, pin a category to
                lock its slot, toggle Active to show or hide it on the grid, and set
                Platform to limit a category to one app. The internal slug never changes.
                Changes go live after you press Save.
            </Typography>

            {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className={classes.chevronCell} />
                                <TableCell className={classes.posCell}>#</TableCell>
                                <TableCell className={classes.logoCell}>Logo</TableCell>
                                <TableCell>Display name</TableCell>
                                <TableCell>Parent</TableCell>
                                <TableCell className={classes.pinnedCell}>Pinned</TableCell>
                                <TableCell className={classes.activeCell}>Active</TableCell>
                                <TableCell className={classes.platformCell}>
                                    <Tooltip title="Which app shows this category. Older builds that send no platform see the All categories only.">
                                        <span>Platform</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell className={classes.arrows}>Drag</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((row, index) => (
                                <React.Fragment key={row.id}>
                                <TableRow
                                    className={[
                                        isDirty(row, index) ? classes.dirtyRow : "",
                                        row.is_active ? "" : classes.inactiveRow,
                                        dragOverIndex === index ? classes.dragOverRow : "",
                                    ].join(" ").trim()}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        onRowDragOver(index);
                                    }}
                                    onDrop={() => {
                                        dragItem.current = null;
                                        setDragOverIndex(null);
                                    }}
                                >
                                    <TableCell className={classes.chevronCell}>
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                setExpandedId(expandedId === row.id ? null : row.id)
                                            }
                                            aria-label="expand brands"
                                        >
                                            {expandedId === row.id ? (
                                                <ExpandMoreIcon fontSize="small" />
                                            ) : (
                                                <ChevronRightIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </TableCell>
                                    <TableCell className={classes.posCell}>{index + 1}</TableCell>
                                    <TableCell className={classes.logoCell}>
                                        {row.category_logo ? (
                                            <Avatar
                                                src={row.category_logo}
                                                alt={row.display_name}
                                                variant="rounded"
                                            />
                                        ) : (
                                            <Avatar variant="rounded">
                                                {row.display_name?.[0] || "?"}
                                            </Avatar>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === row.id ? (
                                            <Box display="flex" alignItems="center">
                                                <TextField
                                                    className={classes.nameInput}
                                                    value={editingName}
                                                    onChange={(e) => setEditingName(e.target.value)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter") confirmEdit(index);
                                                        if (e.key === "Escape") cancelEdit();
                                                    }}
                                                    size="small"
                                                    autoFocus
                                                />
                                                <IconButton
                                                    size="small"
                                                    color="primary"
                                                    onClick={() => confirmEdit(index)}
                                                    aria-label="confirm rename"
                                                >
                                                    <CheckIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={cancelEdit}
                                                    aria-label="cancel rename"
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ) : (
                                            <Box display="flex" alignItems="center">
                                                <span>{row.display_name}</span>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => startEdit(row)}
                                                    aria-label="edit display name"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        )}
                                    </TableCell>
                                    <TableCell className={row.parent_category_id ? classes.parentCell : classes.topLevelCell}>
                                        {parentName(row)}
                                    </TableCell>
                                    <TableCell className={classes.pinnedCell}>
                                        <Tooltip title={row.is_pinned ? "Pinned — click to unpin" : "Not pinned — click to pin"}>
                                            <IconButton
                                                size="small"
                                                onClick={() => togglePin(index)}
                                                aria-label={row.is_pinned ? "unpin category" : "pin category"}
                                            >
                                                {row.is_pinned ? (
                                                    <BookmarkIcon fontSize="small" color="primary" />
                                                ) : (
                                                    <BookmarkBorderIcon fontSize="small" />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell className={classes.activeCell}>
                                        <Tooltip title={row.is_active ? "Active — showing on grid" : "Inactive — hidden from grid"}>
                                            <Switch
                                                checked={row.is_active}
                                                onChange={() => toggleActive(index)}
                                                color="primary"
                                                size="small"
                                                inputProps={{ "aria-label": "toggle active" }}
                                            />
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell className={classes.platformCell}>
                                        <Select
                                            value={row.platform}
                                            onChange={(e) =>
                                                changePlatform(index, e.target.value as string)
                                            }
                                            disableUnderline
                                            margin="dense"
                                            inputProps={{ "aria-label": "category platform" }}
                                        >
                                            {PLATFORM_OPTIONS.map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell className={classes.arrows}>
                                        <span
                                            draggable
                                            onDragStart={() => {
                                                dragItem.current = index;
                                                setDragOverIndex(index);
                                            }}
                                            onDragEnd={() => {
                                                dragItem.current = null;
                                                setDragOverIndex(null);
                                            }}
                                            style={{ cursor: "grab", display: "inline-flex", color: "#888" }}
                                            aria-label="drag to reorder"
                                        >
                                            <DragIndicatorIcon fontSize="small" />
                                        </span>
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell
                                        style={{ paddingBottom: 0, paddingTop: 0 }}
                                        colSpan={9}
                                    >
                                        <Collapse
                                            in={expandedId === row.id}
                                            timeout="auto"
                                            unmountOnExit
                                        >
                                            <CategoryBrandPanel category={row} />
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
            {!embedded && <Notification />}
        </div>
    );
};

export default ShopGridCategories;
